import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  PaginationState,
  Table,
  TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import { EntityTypes } from 'constants/entities';
import { listRequest, reportRequest, reportExportRequest } from 'core/services/admin';
import { IReport, ReportType, exportReportData, updatePageIndex } from 'core/slices/report';
import { ISearchReducer } from 'core/slices/search';
import { useAppDispatch, useAppSelector } from 'core/store/hooks';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { LoadingSpinner } from '../Loading/loadingspinner';
import PrimaryButton from '../Button/PrimaryButton';

export function ReportTable({
  data,
  columns,
  type,
  onClickRow,
  parentEntityId,
  parentEntityType,
  reportType,
  days,
  size = 20,
  paginate = true,
  sort,
  isExpanded,
  expandedColumns,
  expandedDataAttribute,
  expandedType,
  expandedReportType,
  expandedColAttribute,
  canExport,
}: {
  data: any[];
  columns: ColumnDef<any, any>[];
  type?: EntityTypes;
  days?: number;
  size?: number;
  sort?: number;
  paginate?: boolean;
  onClickRow?: (row) => void;
  reportType?: ReportType;
  parentEntityId?: number;
  parentEntityType?: EntityTypes;
  isExpanded?: boolean;
  expandedType?: EntityTypes;
  expandedColAttribute?: string;
  expandedDataAttribute?: string;
  expandedReportType?: ReportType;
  canExport?: boolean;
  expandedColumns?: any; // ColumnDef<any, any>[] | (a?: string) => ColumnDef<any, any>[];
}) {
  const dispatch = useAppDispatch();
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: size,
  });

  const search: ISearchReducer = useAppSelector(state => state.core.search);
  const [expanded, setExpanded] = useState<any>({});
  const reportData: IReport = useAppSelector(state => state.core.report.reports[reportType]);
  const totalRecords = useMemo(
    () =>
      data
        ? reportData && !search.term?.length && reportData.totalRecords > data.length
          ? reportData.totalRecords
          : data.length
        : 0,
    [data, reportData, search.term?.length],
  );
  const totalPages = Math.ceil(totalRecords / pageSize);
  const loading = useMemo(
    () => (reportData && reportData.loading ? reportData.loading[pageIndex] : false),
    [pageIndex, reportData],
  );
  const customReport = useAppSelector(state => state.core.report.custom);
  const prevPageRef = useRef<number>();

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize],
  );

  useEffect(() => {
    setPagination({ pageIndex: 0, pageSize: size });
  }, [search.term, size]);

  const request = useCallback(
    pageIndex => {
      if (prevPageRef.current === pageIndex) return;

      prevPageRef.current = pageIndex;
      dispatch(updatePageIndex({ type: reportType, pageIndex }));

      dispatch(
        reportType
          ? reportRequest({
              type,
              reportType,
              parentEntityId,
              parentEntityType,
              filter: customReport.filter,
              search: search.term,
              sort: sort || search.sort,
              pagerInfo: {
                page: pageIndex,
                pageCount: pageSize,
              },
            })
          : listRequest({
              type,
              parentEntityId,
              parentEntityType,
              search: search.term,
              sort: sort || search.sort,
              pagerInfo: {
                page: pageIndex,
                pageCount: pageSize,
              },
            }),
      );
    },

    [dispatch, reportType, type, parentEntityId, parentEntityType, customReport.filter, search, sort, pageSize],
  );

  useEffect(() => {
    request(pageIndex ?? 0);
  }, [dispatch, pageIndex, reportType, request]);

  const pagedData = useMemo(
    () => data.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize),
    [data, pageIndex, pageSize],
  );

  const options: TableOptions<any> = {
    data: pagedData,
    columns,
    state: {
      pagination,
      expanded,
    },
    onPaginationChange: setPagination,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    onExpandedChange: setExpanded,
    getExpandedRowModel: getExpandedRowModel(),
  };
  const table: Table<any> = useReactTable(options);

  const next = () => table.nextPage();
  const prev = () => table.previousPage();

  const nexPrevClass =
    ' relative ml-2 inline-flex items-center px-2 py-2 border border-gray-300 text-sm font-medium shadow-sm rounded-full text-gray-700 hover:bg-gray-50';

  return (
    <div className="px-2 w-full align-middle shadow-sm mb-10 ">
      <div className=" w-full mt-10 ring-1 ring-slate-200 dark:ring-0 overflow-x-auto  overflow-y-hidden bg-white dark:bg-howio-900 sm:mx-0 sm:rounded-lg">
        <table className="w-full divide-y dark:divide-slate-700  divide-slate-200">
          <thead className="text-sm w-full ">
            {table.getHeaderGroups().map((headerGroup, i) => (
              <tr key={i}>
                {headerGroup.headers.map((header, ind) => (
                  <th scope="col" className="px-5 py-3.5 " key={ind} colSpan={header.colSpan}>
                    {header.isPlaceholder ? null : (
                      <div
                        {...{
                          className: header.column.getCanSort()
                            ? 'cursor-pointer justify-start items-start select-none flex truncate'
                            : 'truncate justify-start text-left items-start',
                          //  onClick: header.column.getToggleSortingHandler(),
                        }}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {{
                          asc: <ChevronUpIcon className="ml-2 w-4 h-4 text-white" />,
                          desc: <ChevronDownIcon className="ml-2 w-4 h-4 text-white" />,
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y dark:divide-slate-800  divide-slate-100">
            {table.getRowModel().rows.map((row, i) => {
              return (
                <>
                  <tr
                    className={
                      expandedType && !row.getIsExpanded()
                        ? 'hover:bg-howio-purple/50 cursor-pointer'
                        : row.getIsExpanded()
                        ? 'bg-howio-purple rounded-t-md '
                        : ''
                    }
                    key={i}
                    onClick={e => (expandedType ? row.toggleExpanded() : onClickRow ? onClickRow(row) : null)}
                  >
                    {row.getVisibleCells().map((cell, i) => (
                      <td key={i} className={'text-md px-5 py-3 text-gray-900 dark:text-white'}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                  {row.getIsExpanded() && (
                    <tr className="bg-howio-purple  px-2 rounded-b-md">
                      <td colSpan={6} className="w-full mx-2 ">
                        <ReportTable
                          reportType={expandedReportType}
                          canExport={expandedReportType === ReportType.ORG_USER_VARIABLE}
                          type={expandedType}
                          isExpanded={true}
                          parentEntityId={row.original.id}
                          data={row.original[expandedDataAttribute]}
                          columns={
                            expandedColAttribute
                              ? expandedColumns(row.original[expandedColAttribute])
                              : expandedColumns()
                          }
                        />
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="w-full pt-2 overflow-x-autosm:mx-0 ">
        {paginate ? (
          <nav
            className="dark:bg-howio-900 dark:text-gray-300 rounded-xl bg-gray-50 px-4 py-3 flex items-center justify-between  sm:px-6"
            aria-label="Pagination"
          >
            <div className="hidden sm:flex items-center">
              <p className="text-sm mr-5">
                <span className="font-medium ">{`Page ${
                  table.getState().pagination.pageIndex + 1
                } / ${totalPages}`}</span>
                {totalRecords ? <span className="font-medium ml-5">{`${totalRecords} Results`}</span> : null}
              </p>
              {loading && <LoadingSpinner />}
            </div>
            <div className="flex-1 flex text-gray-800  justify-between sm:justify-end">
              {canExport && (
                <PrimaryButton
                  className="rounded border-gray-400 border-2 text-sm dark:text-white"
                  label={`Export CSV`}
                  onClick={e => exportReportCSV()}
                />
              )}

              <div onClick={prev} className={nexPrevClass}>
                <span className="sr-only">Previous</span>
                <ChevronLeftIcon className="h-5 w-5 dark:text-white" aria-hidden="true" />
              </div>

              <div onClick={next} className={nexPrevClass}>
                <span className="sr-only">Next</span>
                <ChevronRightIcon className="h-5 w-5 dark:text-white" aria-hidden="true" />
              </div>
            </div>
          </nav>
        ) : null}
      </div>
    </div>
  );
}
